import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { fetchApi } from "@/api/fetchApi";
import { useUser } from "@/api/queries";
import { ErrorPanel, errorText } from "@/components/ErrorPanel";
import { Spinner } from "@/components/Spinner";
import { ThemeButton } from "@/components/ThemeButton";
import { GenerateProductKeysRequest } from "@/generated/backend/v1/messages";
import "./admin.css";

/**
 * Admin (ported from the legacy ProductKeys page): product-key generation, key assignments and
 * company analytics. The backend allows these to the super admin only; blog admin is the site's
 * (/admin/blog). Plain JSON endpoints, as before.
 */
export type Assignment = Record<string, string>;
export type CompanyAnalytics = Record<string, { totalUsers: number; totalPremiumUsers: number }>;

const PAGE_SIZE = 8;

export const adminKeys = {
  assignments: ["admin", "assignments"] as const,
  analytics: ["admin", "analytics"] as const,
};

/** RFC 4180 CSV of the assignments table (the legacy "Export to CSV"). */
export function toCsv(rows: Assignment[]): string {
  if (!rows.length) return "";
  const cols = Object.keys(rows[0]);
  const cell = (v: unknown) => {
    const s = String(v ?? "");
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [cols.map(cell).join(","), ...rows.map((r) => cols.map((c) => cell(r[c])).join(","))].join(
    "\r\n",
  );
}

function downloadCsv(rows: Assignment[]) {
  const blob = new Blob([toCsv(rows)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "product-key-assignments.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function GenerateKeys() {
  const client = useQueryClient();
  const [count, setCount] = useState("10");
  const generate = useMutation({
    mutationFn: (numKeys: number) =>
      fetchApi<string[]>("/product-keys/generate", {
        body: GenerateProductKeysRequest.create({ numKeys }),
        request: GenerateProductKeysRequest,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin"] });
    },
  });
  const n = Number(count);
  const valid = Number.isInteger(n) && n > 0 && n <= 1000;
  return (
    <div className="card pad" data-testid="admin-generate">
      <h2 style={{ marginBottom: 4 }}>Generate product keys</h2>
      <p className="mut" style={{ marginBottom: 14 }}>
        Keys are unassigned until someone enters one in Settings.
      </p>
      <form
        className="wp-admin-row"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) generate.mutate(n);
        }}
      >
        <label className="wp-field">
          How many
          <input
            type="number"
            min={1}
            max={1000}
            value={count}
            onChange={(e) => setCount(e.target.value)}
            data-testid="admin-count"
          />
        </label>
        <button
          type="submit"
          className="wp-btn"
          disabled={!valid || generate.isPending}
          data-testid="admin-generate-submit"
        >
          {generate.isPending ? "Generating…" : "Generate"}
        </button>
      </form>
      {generate.isError && (
        <p className="wp-form-err" role="alert">
          {errorText(generate.error)}
        </p>
      )}
      {!!generate.data?.length && (
        <div className="wp-admin-keys" data-testid="admin-new-keys">
          <div className="wp-admin-row">
            <b>{generate.data.length} new keys</b>
            <button
              type="button"
              className="wp-btn g"
              onClick={() => void navigator.clipboard?.writeText(generate.data!.join("\n"))}
            >
              Copy all
            </button>
            <button type="button" className="wp-btn g" onClick={() => generate.reset()}>
              Clear
            </button>
          </div>
          <ul>
            {generate.data.map((k) => (
              <li key={k}>
                <code>{k}</code>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Assignments() {
  const query = useQuery({
    queryKey: adminKeys.assignments,
    queryFn: () => fetchApi<Assignment[]>("/product-keys/assignments"),
  });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const rows = useMemo(() => query.data ?? [], [query.data]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => Object.values(r).some((v) => String(v).toLowerCase().includes(q)));
  }, [rows, search]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const shown = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const cols = rows.length ? Object.keys(rows[0]) : [];

  return (
    <div className="card pad" data-testid="admin-assignments">
      <div className="wp-admin-row" style={{ marginBottom: 12 }}>
        <h2 style={{ flex: 1 }}>Product key assignments</h2>
        <button
          type="button"
          className="wp-btn g"
          disabled={!rows.length}
          onClick={() => downloadCsv(rows)}
          data-testid="admin-csv"
        >
          Export to CSV
        </button>
      </div>
      {query.isPending ? (
        <Spinner label="Loading assignments" />
      ) : query.isError ? (
        <ErrorPanel error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <>
          <label className="wp-field" style={{ maxWidth: 360 }}>
            Search
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="A key, an email or a company"
              data-testid="admin-search"
            />
          </label>
          <div className="wp-admin-table">
            <table>
              <thead>
                <tr>
                  {cols.map((c) => (
                    <th key={c}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.length ? (
                  shown.map((r, i) => (
                    <tr key={`${r[cols[0]]}-${i}`}>
                      {cols.map((c) => (
                        <td key={c}>{r[c]}</td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={Math.max(1, cols.length)}>No assignments</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className="wp-admin-row wp-admin-pages">
              <button
                type="button"
                className="wp-btn g"
                disabled={current <= 1}
                onClick={() => setPage(current - 1)}
              >
                Previous
              </button>
              <span className="mut">
                Page {current} of {pages}
              </span>
              <button
                type="button"
                className="wp-btn g"
                disabled={current >= pages}
                onClick={() => setPage(current + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Analytics() {
  const query = useQuery({
    queryKey: adminKeys.analytics,
    queryFn: () => fetchApi<CompanyAnalytics>("/company/analytics"),
  });
  const entries = Object.entries(query.data ?? {}).sort((a, b) => a[0].localeCompare(b[0]));
  return (
    <div className="card pad" data-testid="admin-analytics">
      <h2 style={{ marginBottom: 12 }}>Registered companies</h2>
      {query.isPending ? (
        <Spinner label="Loading companies" />
      ) : query.isError ? (
        <ErrorPanel error={query.error} onRetry={() => query.refetch()} />
      ) : entries.length ? (
        <div className="wp-admin-table">
          <table>
            <thead>
              <tr>
                <th>Company</th>
                <th>Users</th>
                <th>Premium users</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(([name, a]) => (
                <tr key={name}>
                  <td>{name}</td>
                  <td className="tnum">{a.totalUsers}</td>
                  <td className="tnum">{a.totalPremiumUsers}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mut">No companies yet.</p>
      )}
    </div>
  );
}

export function AdminPage() {
  const { data: user } = useUser();
  const superAdmin = !!user?.isSuperAdmin;
  return (
    <div id="app" data-testid="page-admin">
      <div className="nav">
        <div className="nav-in">
          <Link to="/app" className="brand" aria-label="WealthyPlanet home">
            <span className="mk" />
            Wealthy<span>Planet</span>
          </Link>
          <span className="tab on">Admin</span>
          <div className="r">
            <ThemeButton />
            <Link className="restart" to="/app">
              Back to your plan
            </Link>
          </div>
        </div>
      </div>
      <main>
        <section className="page on wp-admin">
          <h1 style={{ fontSize: 26, fontWeight: 600, marginBottom: 4 }}>Admin</h1>
          <p style={{ color: "var(--ink2)", marginBottom: 22 }}>
            Product keys, who has them, and which companies are signed up.{" "}
            <Link className="wp-link" to="/admin/blog">
              Manage the blog
            </Link>
          </p>
          {superAdmin ? (
            <>
              <GenerateKeys />
              <Assignments />
              <Analytics />
            </>
          ) : (
            <div className="card pad" data-testid="admin-not-super">
              <p className="mut">
                Product keys and company analytics are for the WealthyPlanet super admin.
              </p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
